import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, RefreshCw, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import './AdminPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const AdminPanel = () => {
  const [unprocessedCount, setUnprocessedCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/stats`);
      setStats(response.data);
      setUnprocessedCount(response.data.unprocessed_files || 0);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const processInvoices = async (mode = 'new') => {
    setProcessing(true);
    setProcessStatus({ type: 'info', message: 'Starting OCR processing...' });
    
    try {
      const response = await axios.post(`${API_BASE}/api/admin/process`, {
        mode: mode, // 'new', 'failed', 'all'
        limit: 10   // Process 10 at a time
      });
      
      setProcessStatus({
        type: 'success',
        message: `Processed ${response.data.processed} invoices successfully!`
      });
      
      // Refresh stats
      await fetchStats();
    } catch (error) {
      setProcessStatus({
        type: 'error',
        message: `Error: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setProcessing(false);
    }
  };

  const runReconciliation = async () => {
    setProcessing(true);
    setProcessStatus({ type: 'info', message: 'Running reconciliation...' });
    
    try {
      const response = await axios.post(`${API_BASE}/api/admin/reconcile`);
      setProcessStatus({
        type: 'success',
        message: `Reconciliation complete: ${response.data.fixes_applied} fixes applied`
      });
      await fetchStats();
    } catch (error) {
      setProcessStatus({
        type: 'error',
        message: `Error: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <FileText size={24} />
            <div className="stat-content">
              <div className="stat-value">{stats.total_files}</div>
              <div className="stat-label">Total Files</div>
            </div>
          </div>
          
          <div className="stat-card">
            <CheckCircle size={24} />
            <div className="stat-content">
              <div className="stat-value">{stats.processed_invoices}</div>
              <div className="stat-label">Processed</div>
            </div>
          </div>
          
          <div className="stat-card">
            <AlertCircle size={24} />
            <div className="stat-content">
              <div className="stat-value">{stats.unprocessed_files}</div>
              <div className="stat-label">Unprocessed</div>
            </div>
          </div>
          
          <div className="stat-card">
            <RefreshCw size={24} />
            <div className="stat-content">
              <div className="stat-value">{stats.needs_review}</div>
              <div className="stat-label">Needs Review</div>
            </div>
          </div>
        </div>
      )}
      
      {processStatus && (
        <div className={`status-message ${processStatus.type}`}>
          {processStatus.message}
        </div>
      )}
      
      <div className="action-section">
        <h3>OCR Processing</h3>
        <p>Process invoices through OCR (Azure Form Recognizer)</p>
        
        <div className="action-buttons">
          <button
            className="action-btn primary"
            onClick={() => processInvoices('new')}
            disabled={processing || unprocessedCount === 0}
          >
            <Upload size={20} />
            Process New Files ({unprocessedCount})
          </button>
          
          <button
            className="action-btn secondary"
            onClick={() => processInvoices('failed')}
            disabled={processing}
          >
            <RefreshCw size={20} />
            Retry Failed
          </button>
        </div>
      </div>
      
      <div className="action-section">
        <h3>Reconciliation</h3>
        <p>Apply business rules and fix known issues</p>
        
        <div className="action-buttons">
          <button
            className="action-btn primary"
            onClick={runReconciliation}
            disabled={processing}
          >
            <RefreshCw size={20} />
            Run Reconciliation
          </button>
        </div>
      </div>
      
      <div className="recommendation-box">
        <h3>💡 Recommendation: Process on Azure</h3>
        <p>For production OCR processing, we recommend:</p>
        <ul>
          <li><strong>Azure:</strong> Better for production - consistent performance, no local resource usage</li>
          <li><strong>Local:</strong> Good for testing small batches or debugging</li>
        </ul>
        <p>Current setup uses Azure Form Recognizer which provides better accuracy for Croatian invoices.</p>
      </div>
    </div>
  );
};

export default AdminPanel;